import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import { Cookie, Shield, CheckCircle, AlertCircle, Settings } from "lucide-react";

export default function CookieRichtlinie() {
  const { t } = useTranslation();
  const { lang = 'de' } = useParams();
  const { getPath } = useLocalizedPath();

  const necessaryItems = t('cookiePolicy.settings.necessary.items', { returnObjects: true }) || [];

  return (
    <div data-testid="cookie-richtlinie-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h1 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white">
            {t('cookiePolicy.title')}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 bg-[#F7FAFC]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          {/* Cookie Settings Overview Card */}
          <Card className="bg-white border mb-8" data-testid="cookie-settings-card">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-500">
                  <Settings className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold font-['Manrope'] text-[#2D3748]">
                  {t('cookiePolicy.settings.title')}
                </h2>
              </div>

              <p className="text-gray-600 mb-6">
                {t('cookiePolicy.settings.description')}
              </p>

              <div className="space-y-4">
                {/* Necessary cookies */}
                <div className="py-4 px-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <h3 className="font-semibold text-[#2D3748]">
                      {t('cookiePolicy.settings.necessary.title')}
                    </h3>
                    <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                      Aktiv
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('cookiePolicy.settings.necessary.description')}
                  </p>
                  <div className="text-sm text-gray-600">
                    <strong>{t('cookiePolicy.settings.necessary.examples')}</strong>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      {Array.isArray(necessaryItems) && necessaryItems.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="py-4 px-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-gray-400" />
                    <h3 className="font-semibold text-[#2D3748]">
                      {t('cookiePolicy.settings.analytics.title')}
                    </h3>
                    <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">
                      Nicht aktiv
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {t('cookiePolicy.settings.analytics.description')}
                  </p>
                  <p className="text-sm text-orange-600 font-medium">
                    {t('cookiePolicy.settings.analytics.note')}
                  </p>
                </div>

                {/* Marketing cookies */}
                <div className="py-4 px-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-gray-400" />
                    <h3 className="font-semibold text-[#2D3748]">
                      {t('cookiePolicy.settings.marketing.title')}
                    </h3>
                    <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">
                      Nicht aktiv
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {t('cookiePolicy.settings.marketing.description')}
                  </p>
                  <p className="text-sm text-orange-600 font-medium">
                    {t('cookiePolicy.settings.marketing.note')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cookie Policy Info */}
          <Card className="bg-white border">
            <CardContent className="p-6 md:p-10">
              <div className="prose prose-gray max-w-none">
                {/* Hinweis für nicht-deutsche Versionen */}
                {lang !== 'de' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-8">
                    <p className="text-gray-700 text-sm m-0">
                      {t('cookiePolicy.legalNote')}
                    </p>
                  </div>
                )}

                {/* Was sind Cookies */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Cookie className="h-6 w-6 text-orange-500" />
                    <h2 className="text-2xl font-bold font-['Manrope'] text-[#2D3748] m-0">
                      {t('cookiePolicy.what.title')}
                    </h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {t('cookiePolicy.what.description')}
                  </p>
                </div>

                {/* Welche Cookies verwenden wir */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('cookiePolicy.types.title')}
                  </h3>
                  
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h4 className="font-semibold text-[#2D3748] mb-2">
                      {t('cookiePolicy.types.necessary.title')}
                    </h4>
                    <p className="text-gray-700 text-sm">
                      {t('cookiePolicy.types.necessary.description')}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h4 className="font-semibold text-[#2D3748] mb-2">
                      {t('cookiePolicy.types.analytics.title')}
                    </h4>
                    <p className="text-gray-700 text-sm mb-2">
                      {t('cookiePolicy.types.analytics.description')}
                    </p>
                    <p className="text-orange-600 text-sm font-medium">
                      {t('cookiePolicy.types.analytics.note')}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-[#2D3748] mb-2">
                      {t('cookiePolicy.types.marketing.title')}
                    </h4>
                    <p className="text-gray-700 text-sm mb-2">
                      {t('cookiePolicy.types.marketing.description')}
                    </p>
                    <p className="text-orange-600 text-sm font-medium">
                      {t('cookiePolicy.types.marketing.note')}
                    </p>
                  </div>
                </div>

                {/* Cookies verwalten */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('cookiePolicy.manage.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {t('cookiePolicy.manage.description')}
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Chrome:</strong> {t('cookiePolicy.manage.browsers.chrome')}</li>
                    <li><strong>Firefox:</strong> {t('cookiePolicy.manage.browsers.firefox')}</li>
                    <li><strong>Safari:</strong> {t('cookiePolicy.manage.browsers.safari')}</li>
                    <li><strong>Edge:</strong> {t('cookiePolicy.manage.browsers.edge')}</li>
                  </ul>
                </div>

                {/* Rechtsgrundlage */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="h-6 w-6 text-orange-500" />
                    <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] m-0">
                      {t('cookiePolicy.legalBasis.title')}
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    {t('cookiePolicy.legalBasis.description')}
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    {t('cookiePolicy.legalBasis.optionalNote')}
                  </p>
                </div>

                {/* Kontakt */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-4">
                    {t('cookiePolicy.questions.title')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('cookiePolicy.questions.description')}
                  </p>
                  <p className="text-gray-700 mt-2">
                    {t('common.email')}:{" "}
                    <a
                      href="mailto:info@mtrenovierung.de"
                      className="text-orange-500 hover:text-orange-600"
                    >
                      info@mtrenovierung.de
                    </a>
                  </p>
                </div>

                {/* Stand */}
                <div>
                  <p className="text-gray-600 text-sm">
                    {t('cookiePolicy.lastUpdated')}
                  </p>
                </div>
              </div>

              {/* Links */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <p className="text-gray-600 text-sm">
                  {t('cookiePolicy.moreInfoText')}{" "}
                  <Link to={getPath('impressum')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('impressum.title')}
                  </Link>
                  ,{" "}
                  <Link to={getPath('privacy')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('privacy.title')}
                  </Link>
                  {" "}&{" "}
                  <Link to={getPath('disclaimer')} className="text-orange-500 hover:text-orange-600 underline">
                    {t('disclaimer.title')}
                  </Link>
                  .
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
